// Curated-playlists home carousel. Replaces the former FeaturedAndCurated grid
// (which showed 5 random tiles + a featured artist). Renders ALL eligible
// curated playlists as an ambient-drift "peek track".
//
// NOTE(port): diverges from the Angular source — Angular renders a static grid
// capped at 4–5 items. This React-side carousel is intentional new behavior.
//
// Motion: the track list is rendered twice; a rAF loop writes
// `transform: translateX(...)` imperatively each frame (no React re-render).
// Drift pauses on hover / keyboard focus (hoverRef) and on touch / hidden-tab
// (touchPaused / tabHidden). prefers-reduced-motion stops the rAF drift loop
// entirely (isDriftActive returns false) while still rendering the doubled
// track (so the layout/scroll remains consistent). See carousel-drift.ts for
// the pure math helpers. [imageLoader] → AppImage;
// [multilineEllipsis] → useMultilineEllipsis (per-item component).

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import type { CuratedPlaylist } from '../../interfaces/curated-playlist';
import AppImage from '../../shared/components/AppImage';
import { useMultilineEllipsis } from '../../shared/hooks/useMultilineEllipsis';
import { padToMinLength } from '../../shared/utils/nav-bar-helper';
import { PIXELATE_IN_OUT_SEARCH_BOXES_ENTER_CLASS } from '../../animations/pixelateInOutSearchBoxes';
import { accumulateNudge, advance, isDriftActive, type DriftGate } from './carousel-drift';

// Pad the per-copy track to at least this many cards so one copy spans the
// widest layout (max-width 1920px ÷ ~160px min card ≈ 12) — prevents gaps.
const MIN_TRACK_CARDS = 12;
// Seconds of drift per card; total duration = cards × this, so velocity is
// constant regardless of how many playlists there are. Higher = slower drift.
const SECONDS_PER_CARD = 10;

export interface CuratedCarouselProps {
  curatedPromptPlaylists: CuratedPlaylist[] | undefined;
  isMobileDevice: boolean;
  searchDropDownHidden: boolean;
  onPromptClicked: (songhuntLink: string) => void;
}

function MultilineEllipsisP({ children }: { children: ReactNode }) {
  const { ref } = useMultilineEllipsis<HTMLParagraphElement>();
  return <p ref={ref} className="multiline-ellipsis">{children}</p>;
}

function CardContents({ playlist, isMobileDevice }: { playlist: CuratedPlaylist; isMobileDevice: boolean }) {
  if (isMobileDevice) {
    return (
      <section className="relative">
        <AppImage src={playlist.promptImageUrl!} alt="" className="opacity-70 !h-[64px]" />
        <div className="absolute top-0 bottom-0 left-0 right-0 text-center bg-black/50 flex items-center justify-center">
          <span className="text-white text-base w-full break-words whitespace-normal">{playlist.shortPrompt}</span>
        </div>
      </section>
    );
  }
  return (
    <>
      <AppImage src={playlist.promptImageUrl!} alt="" />
      <MultilineEllipsisP>{playlist.shortPrompt}</MultilineEllipsisP>
    </>
  );
}

function PlaylistCard({
  playlist,
  isMobileDevice,
  interactive,
  onActivate
}: {
  playlist: CuratedPlaylist;
  isMobileDevice: boolean;
  interactive: boolean;
  onActivate: () => void;
}) {
  // Interactive cards are real buttons; non-interactive ones are duplicated
  // marquee clones — kept fully inert (aria-hidden, no role/tab focus) so
  // assistive tech and keyboard users only ever reach each playlist once.
  const a11yProps = interactive
    ? {
        role: 'button' as const,
        tabIndex: 0,
        'aria-label': `Explore the ${playlist.shortPrompt ?? playlist.name} playlist`,
        onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActivate();
          }
        }
      }
    : { 'aria-hidden': true as const };

  return (
    <div className="playlist" onClick={onActivate} {...a11yProps}>
      <CardContents playlist={playlist} isMobileDevice={isMobileDevice} />
    </div>
  );
}

export default function CuratedCarousel({
  curatedPromptPlaylists,
  isMobileDevice,
  searchDropDownHidden,
  onPromptClicked
}: CuratedCarouselProps) {
  // Drift pauses on touch (this state) and while the tab is hidden — kept as
  // separate sources so restoring a hidden tab can't resume mid-touch.
  const [touchPaused, setTouchPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(() => document.hidden);
  const [reducedMotion, setReducedMotion] = useState(false);

  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(0);
  const pendingNudgeRef = useRef(0);
  const pauseUntilRef = useRef(0);
  const hoverRef = useRef(false);
  const [unitWidth, setUnitWidth] = useState(0);

  // Pause the drift while the tab is hidden to avoid wasted CPU.
  useEffect(() => {
    const onVisibility = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Respect prefers-reduced-motion: the rAF drift loop is disabled (isDriftActive
  // returns false) but the track is still doubled; nudges are applied instantly
  // (instant flag) so manual arrow navigation still works.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Measure one copy's width from the doubled track (`track.scrollWidth / 2`).
  // Re-measures whenever playlists change or the viewport is resized.
  // The track is always doubled regardless of reducedMotion, so reducedMotion
  // is not a dependency here.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => setUnitWidth(track.scrollWidth / 2);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    return () => ro.disconnect();
  }, [curatedPromptPlaylists]);

  // rAF drift loop — writes transform directly to the DOM, no React re-render.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || unitWidth <= 0) return;

    const unitCount = padToMinLength(curatedPromptPlaylists ?? [], MIN_TRACK_CARDS).length;
    const driftSpeed = unitWidth / (unitCount * SECONDS_PER_CARD); // px/s — matches the old CSS velocity

    let last = performance.now();
    let raf = 0;
    const frame = (now: number) => {
      const dtMs = now - last;
      last = now;
      const gate: DriftGate = {
        pauseUntil: pauseUntilRef.current,
        hovered: hoverRef.current,
        touchHeld: touchPaused,
        tabHidden,
        reducedMotion,
      };
      const next = advance({
        pos: posRef.current,
        pendingNudge: pendingNudgeRef.current,
        dtMs,
        driftSpeed,
        driftActive: isDriftActive(now, gate),
        unitWidth,
        easeFraction: 0.15,
        instant: reducedMotion,
      });
      posRef.current = next.pos;
      pendingNudgeRef.current = next.pendingNudge;
      track.style.transform = `translateX(${-next.pos}px)`;
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [unitWidth, curatedPromptPlaylists, touchPaused, tabHidden, reducedMotion]);

  const PAUSE_AFTER_NUDGE_MS = 3000;
  const nudge = (direction: 1 | -1) => {
    const width = viewportRef.current?.clientWidth ?? 0;
    pendingNudgeRef.current = accumulateNudge(pendingNudgeRef.current, direction * width);
    pauseUntilRef.current = performance.now() + PAUSE_AFTER_NUDGE_MS;
  };

  const playlists = curatedPromptPlaylists ?? [];
  const zClass = searchDropDownHidden ? ' !z-10' : '';

  // Nothing to show.
  if (playlists.length === 0) {
    return null;
  }

  // A single card has nothing to loop — render it static and centered.
  if (playlists.length === 1) {
    const playlist = playlists[0];
    return (
      <section className={`curated-carousel curated-carousel--single ${PIXELATE_IN_OUT_SEARCH_BOXES_ENTER_CLASS}${zClass}`}>
        <PlaylistCard
          playlist={playlist}
          isMobileDevice={isMobileDevice}
          interactive
          onActivate={() => onPromptClicked(playlist.songhuntLink)}
        />
      </section>
    );
  }

  const unit = padToMinLength(playlists, MIN_TRACK_CARDS);
  // Two identical copies; the rAF loop handles looping via wrapOffset so the
  // track is always doubled (even under reduced-motion, where the loop simply
  // stays still). The CSS keyframe and `--drift-duration` are no longer used.
  const track = [...unit, ...unit];

  return (
    <section className={`curated-carousel ${PIXELATE_IN_OUT_SEARCH_BOXES_ENTER_CLASS}${zClass}`}>
      <button
        type="button"
        className="curated-arrow curated-arrow--left"
        aria-label="Scroll playlists left"
        onClick={() => nudge(-1)}
      >
        <span aria-hidden="true">‹</span>
      </button>
      <div className="curated-viewport" ref={viewportRef}>
        <div
          className="curated-track"
          ref={trackRef}
          onMouseEnter={() => { hoverRef.current = true; }}
          onMouseLeave={() => { hoverRef.current = false; }}
          onFocus={() => { hoverRef.current = true; }}
          onBlur={() => { hoverRef.current = false; }}
          onTouchStart={() => setTouchPaused(true)}
          onTouchEnd={() => setTouchPaused(false)}
          onTouchCancel={() => setTouchPaused(false)}
        >
          {track.map((playlist, index) => {
            const isClone = index >= unit.length;
            return (
              <PlaylistCard
                key={`${playlist.id}-${index}`}
                playlist={playlist}
                isMobileDevice={isMobileDevice}
                interactive={!isClone}
                onActivate={() => onPromptClicked(playlist.songhuntLink)}
              />
            );
          })}
        </div>
      </div>
      <button
        type="button"
        className="curated-arrow curated-arrow--right"
        aria-label="Scroll playlists right"
        onClick={() => nudge(1)}
      >
        <span aria-hidden="true">›</span>
      </button>
    </section>
  );
}
